import type { HTMLAttributes } from "react";
import { useMonkeysLocaleMessages } from "../../provider/context";

import { BaseSkeleton } from "../base/BaseSkeleton";
import { cn } from "../base/utils";
import { SkeletonScope, SkeletonItem, type SkeletonGroupAnimation } from "../skeleton/motion";

export interface WorkbenchPageSkeletonProps extends HTMLAttributes<HTMLDivElement> {
  animated?: boolean;
  animation?: SkeletonGroupAnimation;
}

export function WorkbenchPageSkeleton({
  animated = true,
  animation,
  className,
  'aria-label': ariaLabel,
  ...props
}: WorkbenchPageSkeletonProps) {
  const loadingLabel = useMonkeysLocaleMessages().loading.spinner;
  const skeleton = (classNames: string) => (
    <BaseSkeleton variant="block" animated={animated} className={classNames} />
  );

  return (
    <SkeletonScope animation={animation} animated={animated}>
      <div
        {...props}
        role="status"
        aria-busy="true"
        aria-label={ariaLabel ?? loadingLabel}
        data-monkeys-component="workbench-page-skeleton"
        className={cn("flex size-full min-h-0 flex-col gap-4 overflow-hidden", className)}
      >
        <div className="flex h-10 shrink-0 items-center justify-between gap-4 rounded-lg border border-border bg-card px-4">
          <div className="flex items-center gap-2">
            {skeleton("h-5 w-20")}
            {skeleton("h-5 w-16")}
            {skeleton("h-5 w-24")}
          </div>
          <div className="flex items-center gap-2">
            {skeleton("h-7 w-20")}
            {skeleton("size-7")}
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-1 gap-4 md:grid-cols-3">
          {skeleton("h-40 md:col-span-2")}
          {skeleton("h-40")}
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(14rem,1fr)]">
          <div className="flex min-h-0 flex-col gap-4 rounded-lg border border-border bg-card p-4">
            {skeleton("h-6 w-40")}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <SkeletonItem key={index} index={index}>
                  <div className="space-y-2">
                    {skeleton("h-4 w-20")}
                    {skeleton("h-9 w-full")}
                  </div>
                </SkeletonItem>
              ))}
            </div>
            {skeleton("min-h-32 flex-1")}
          </div>

          <div className="flex min-h-40 flex-col gap-4 rounded-lg border border-border bg-card p-4">
            {skeleton("h-6 w-28")}
            {skeleton("min-h-28 flex-1")}
            {skeleton("h-9 w-full")}
          </div>
        </div>
      </div>
    </SkeletonScope>
  );
}
